import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import { problems } from './model-check';
import { checkDraft, draftOf, runsAsGroup, specOf } from './model-draft';
import type { BackendClient } from '../bridge/backend-client';
import {
  configurationItems,
  hasArray,
  portNamesText,
  replay,
  replayCounts,
  skippedItems,
  stateItems,
  visitedItems,
  type HistorySource,
  type ReplayEngine,
  type ReplayResult,
} from '../../sandbox/statechart/replay';
import { historyFromBackend } from '../../sandbox/statechart/history-source';

/*
 * THE AI DOOR TO THE REPLAY SANDBOX — `models.simulate`.
 *
 * `models.write` is where the assistant AUTHORS a statechart; this is where it finds out what that
 * statechart would have DONE. It takes a saved model by its id, with the wiring that connects its
 * inputs, and replays a window of recorded history through it in the browser's sandbox. The answer
 * is the driver's own summary paragraph: which states it entered, which it never reached, what it
 * would have written, where it errored.
 *
 * NOTHING HERE TOUCHES A MACHINE. The engine runs in an opaque-origin frame with no network; every
 * write the logic would have made is captured as a trace entry that goes nowhere; the only traffic
 * is the history reads a person with `history:read` could already make from the History tab. That
 * is why the permission is HISTORY_READ and not MODEL_EDIT, and why there is no confirm gate.
 *
 * ITS OWN FILE, not a seventh entry in model-tools.ts, for one reason: its dependencies are not the
 * other tools'. Those reach a gateway's managers and nothing else; this one needs the backend worker
 * (history) and a sandbox (the engine), and the sandbox session is a browser-only module — it
 * imports a Vite-built frame script and builds an iframe. Both arrive through `deps` so a Node test
 * can hand in the real engine in-process and a history made of fixtures, exactly as the driver's
 * own test does. The default implementations are the frame and the worker, loaded on demand.
 *
 * THE DRIVER REFUSES, THIS PASSES IT THROUGH. Every reason a replay cannot be trusted — a document
 * that reads nothing, a row budget exceeded, nothing at all with history — is already a sentence in
 * replay.ts, and the same sentence the manual Simulate panel shows. A refusal is returned as a failed
 * outcome in those words, so the assistant's next call can be a fix rather than a retry. In
 * particular the NO_LIVE_INPUTS gate holds here as it does on write: a replay of an inert model
 * reports "ended in idle" and looks like a pass, which is the one result this tool must never produce.
 *
 * THE DOCUMENT IS CHECKED FIRST, as models.write checks it (model-check.ts). A draft with an error
 * or a name nothing declares is not replayed: the sandbox could not boot the first, and the second
 * would fail in the middle of the replay and read as a model that did nothing. The reply lists every
 * problem with its line, so the author can check a draft here before it writes anything. Warnings go
 * on the summary. A call to Date.now() or Math.random() is one of the errors: a TypeScript model must
 * replay as it ran (Part 3 of docs/hierarchical-models-step-3-plan.md).
 *
 * A SINGLE CONNECTION THE DRIVER COULD NOT REPLAY does not refuse the run: it is set aside and the
 * rest are replayed. The summary says which were left out, and `data.skipped` carries the same
 * list. A reply that reads "across 3 input(s)" over a model with 5 connected inputs must not pass
 * as clean.
 *
 * THE TRACE STAYS IN THIS TAB. The outcome the model reads is the summary; the card in the
 * transcript draws the full replay from a trace kept in memory here, keyed by an id the outcome
 * carries. Nothing is persisted, and nothing is added to any table. After a reload the card still
 * has the summary and the source, and says the replay itself is gone.
 */

export interface ModelSimulateDeps extends GatewayScope {
  backend(): BackendClient | undefined;
  /** The engine to replay in. Absent: the sandbox frame (session.ts), imported on first use.
   *  A test hands in the real engine in-process. */
  engine?: () => Promise<ReplayEngine>;
  /** Where recorded history comes from. Absent: the backend worker's typed reader. */
  history?: (backend: BackendClient, gatewayId: string) => HistorySource;
}

/** A replay this tab ran, kept for its card. */
export interface KeptSimulation {
  scxml: string;
  result: ReplayResult;
}

/*
 * THE LAST FEW REPLAYS, AND NO MORE.
 *
 * A result holds every recorded row it replayed and every trace entry it produced — up to the
 * driver's million-row budget, which is tens of megabytes. A conversation that writes, simulates,
 * revises and simulates again would accumulate one of those per attempt for as long as the tab
 * lived. So this keeps a handful, and weighs them: the oldest go first once there are more than
 * KEEP, or once together they hold more than KEEP_ENTRIES rows and trace entries — about two
 * replays at the driver's budget. The newest is always kept, whatever it weighs, because its card
 * is the one on screen. A card whose replay was forgotten shows what the reload case shows, the
 * summary and the source, with a line saying the picture is gone.
 */
const KEEP = 8;
const KEEP_ENTRIES = 2_000_000;
const kept = new Map<string, KeptSimulation>();
let nextId = 1;

const weight = (k: KeptSimulation): number => k.result.samples.length + k.result.trace.length;

export function keepSimulation(scxml: string, result: ReplayResult): string {
  const id = `sim-${nextId++}`;
  kept.set(id, { scxml, result });
  let total = 0;
  for (const k of kept.values()) total += weight(k);
  for (const [oldest, k] of kept) {
    if (kept.size === 1 || (kept.size <= KEEP && total <= KEEP_ENTRIES)) break;
    kept.delete(oldest);
    total -= weight(k);
  }
  return id;
}

export function simulationById(id: string): KeptSimulation | undefined {
  return kept.get(id);
}

const MS_NS = 1_000_000n;
const DEFAULT_MINUTES = 60;
/** Enough captured writes for the next turn to reason about; the summary already names every
 *  distinct path, so past this the list is a spreadsheet handed to a model. */
const MAX_WRITES_IN_DATA = 50;
/** How many symbols the reply of a group with an array names when the historian recorded nothing; it counts the rest. */
const SYMBOLS_LISTED = 10;

const noBackend: ToolOutcome = { ok: false, message: 'The backend is not ready yet — try again in a moment.' };

/** The frame and its worker: the default engine, and the viewer the card will need the moment the
 *  result lands. Both are their own chunks, and most conversations never replay anything. */
async function frameEngine(): Promise<ReplayEngine> {
  const [{ StatechartSandbox }] = await Promise.all([
    import('../../sandbox/statechart/session'),
    import('../components/model/vitesse-statechart-replay'),
  ]);
  return new StatechartSandbox();
}

/**
 * `[fromMs, toMs)` from the arguments: an explicit pair, else the last `minutesAgo` minutes.
 *
 * A time of 0 is NOT GIVEN, the same as a missing key. A model that fills every optional field
 * sends 0 for the times it did not mean: GPT-5.4 mini sent `minutesAgo: 60, fromMs: 0, toMs: 0`.
 * Read as times, those zeros refused the hour it asked for, and the retry cost the last tool round
 * of the turn. The epoch is never the edge of a recorded window, so nothing real is lost.
 */
function resolveWindow(args: Record<string, unknown>, nowMs: number): { t0Ms: number; t1Ms: number } | { refusal: string } {
  const has = (k: string) => args[k] !== undefined && args[k] !== null;
  const time = (k: 'fromMs' | 'toMs'): number | undefined => (has(k) && args[k] !== 0 ? Number(args[k]) : undefined);
  const t0Ms = time('fromMs');
  const t1Ms = time('toMs');
  if (t0Ms !== undefined || t1Ms !== undefined) {
    if (t0Ms === undefined || t1Ms === undefined || !Number.isFinite(t0Ms) || !Number.isFinite(t1Ms)) {
      return { refusal: 'fromMs and toMs must both be given, as milliseconds since the epoch — or give neither and use minutesAgo.' };
    }
    if (!(t1Ms > t0Ms)) return { refusal: 'Refused: the window ends before it starts — toMs must be later than fromMs.' };
    return { t0Ms, t1Ms };
  }
  const minutes = has('minutesAgo') ? Number(args.minutesAgo) : DEFAULT_MINUTES;
  if (!Number.isFinite(minutes) || minutes <= 0) return { refusal: 'minutesAgo must be a positive number of minutes.' };
  return { t0Ms: nowMs - minutes * 60_000, t1Ms: nowMs };
}

/** Structured-clone-safe to JSON-safe: the trace is stamped in bigint nanoseconds and the outcome
 *  is archived as JSON, which has no bigint. */
const plain = (v: unknown): unknown => (typeof v === 'bigint' ? v.toString() : v);
const toMs = (ns: bigint): number => Number(ns / MS_NS);

export function registerModelSimulateTool(deps: ModelSimulateDeps): () => void {
  const simulate: ClientTool = {
    name: 'models.simulate',
    // History reads only; the engine runs here, in a sandbox that reaches nothing.
    permission: Permission.HISTORY_READ,
    mutates: false,
    description:
      'Replay a saved model against recorded history to see what it WOULD have done, without deploying or writing anything. ' +
      'Pass its modelId: it replays the model as saved, every machine of a group, with its own wiring. A change is saved with ' +
      'models.write first; the model is stopped until a person starts it, so saving it to replay it changes nothing on a machine. ' +
      "The reply says which states it entered and how often (a child's as child/state, an array's as sensors/*/state), which it never reached, what it would " +
      'have written, and any script error: read it before telling the user a model is done. A state never entered over real data ' +
      'is worth a sentence to them. Nothing reaches a machine. The model is checked first: if it has errors, it is not replayed ' +
      'and the reply lists every problem with its line.',
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The saved model, as returned by models.list.' },
        minutesAgo: { type: 'number', description: 'Replay the last N minutes. Defaults to 60. Ignored when fromMs/toMs are given.' },
        fromMs: { type: 'number', description: 'Start of an explicit window, milliseconds since the epoch. Give toMs with it.' },
        toMs: { type: 'number', description: 'End of an explicit window, milliseconds since the epoch.' },
      },
      required: ['modelId'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const backend = deps.backend();
      if (!backend) return noBackend;

      // ── the model, as saved ──────────────────────────────────────────────
      /*
       * A SAVED MODEL, NOT A DRAFT (Part 5 of docs/hierarchical-models-step-3-plan.md). With the edit
       * tool, a draft is a stopped model: it is saved, checked, and replayed as the relay would run
       * it, every machine of a group with its wiring. A document handed in as text could not be a
       * group, and could differ from what is saved.
       */
      const modelId = String(args.modelId ?? '').trim();
      if (!modelId) return { ok: false, message: 'Give modelId: the saved model to replay. Save a change with models.write first.' };
      const view = entry.models.get(modelId);
      if (!view) {
        const known = entry.models.all.map((m) => m.id);
        return {
          ok: false,
          message: `No model "${modelId}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ''}`,
        };
      }
      let source;
      try {
        source = await entry.models.getSource(modelId);
      } catch (e) {
        return {
          ok: false,
          message: `Could not read the source of "${view.name}": ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      const draft = draftOf(source, view);
      if (!draft) {
        return {
          ok: false,
          message: `"${view.name}" is a group, and this relay is older than Part 5 of the Step 3 plan, so it does not send the group's templates to replay.`,
        };
      }
      const scxml = draft.scxml;
      const outputs = draft.iface.outputs.map((p) => p.name);
      const label = view.name;

      // ── the check, before anything boots ──────────────────────────────────
      const checked = await checkDraft(draft, entry.models.all, modelId);
      if (checked && checked.refusingCount > 0) {
        return {
          ok: false,
          message:
            `Not replayed: "${view.name}" has ${problems(checked.refusingCount)} that the relay would refuse or that would fail when the model runs. ` +
            `Fix every one with models.write, then replay it.\n${checked.refusing}`,
          retryable: false,
        };
      }
      const warnings = checked?.warnings ?? '';

      // ── the window ───────────────────────────────────────────────────────
      const win = resolveWindow(args, Date.now());
      if ('refusal' in win) return { ok: false, message: win.refusal };

      // ── the replay ───────────────────────────────────────────────────────
      const history = (deps.history ?? ((b, g) => historyFromBackend(b.api, g)))(backend, gw.value.id);
      let engine: ReplayEngine;
      try {
        engine = await (deps.engine ?? frameEngine)();
      } catch (e) {
        return {
          ok: false,
          message: `The statechart sandbox could not start: ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      try {
        // A model that runs as a group replays as one: its children, or its root's own wiring (a
        // group of one). A model with neither reads nothing from outside, and the replay says so.
        const group = runsAsGroup(draft)
          ? specOf(draft, {
              root: draft.scxml,
              templates: Object.fromEntries(Object.entries(draft.templates).map(([n, t]) => [n, t.scxml])),
            })
          : undefined;
        const out = await replay({ scxml, outputs, t0Ms: win.t0Ms, t1Ms: win.t1Ms, ...(group ? { group } : {}) }, engine, history);
        // The driver's own words, unchanged: they are what the next attempt is corrected against.
        // Not retryable as-is — the same document over the same window refuses the same way.
        if (!out.ok) return { ok: false, message: out.refusal, retryable: false };

        const traceId = keepSimulation(scxml, out);
        // The summary's own counts: real moves, not one per event handled.
        const { moves, movesByEvent, writes, errors } = replayCounts(out.trace);
        /*
         * A WINDOW WITH NOTHING IN IT IS SAID, NOT IMPLIED. The summary's "0 recorded change(s)"
         * is true and easy to read past; a model that could not move because nothing happened
         * looks, in every other number, like a model that had nothing to do.
         */
        // A group with an array folds the entries of each array in the reply and in the data, and caps its
        // lists: each machine has its own input, states and PLC symbol, so a row of 256 would fill the reply.
        // The trace that the card draws keeps every machine.
        const arrayed = hasArray(out);
        const symbols = out.inputs.map((i) => i.symbolDecoded);
        const empty =
          out.rowCount === 0
            ? ` The historian recorded nothing for ${arrayed && symbols.length > SYMBOLS_LISTED ? `${symbols.slice(0, SYMBOLS_LISTED).join(', ')} and ${symbols.length - SYMBOLS_LISTED} more` : symbols.join(', ')} in this window, so the model had no event to react to and could not move — try a wider window, or a controller that has this data.`
            : '';
        const unknown = out.inputs.filter((i) => i.seed === null).map((i) => i.name ?? i.path);
        return {
          ok: true,
          message:
            (label ? `Replayed "${label}". ` : '') +
            out.summary +
            empty +
            ' The replay is drawn in the transcript beside this call, so describe what the logic decided rather than where to look. Nothing was deployed and nothing reached a machine.' +
            (warnings.trim() ? `\nWarnings about the model; the replay ran, so fix one only if it is a mistake:\n${warnings}` : ''),
          data: {
            traceId,
            modelId,
            window: { fromMs: toMs(out.window.t0Ns), toMs: toMs(out.window.t1Ns) },
            rows: out.rowCount,
            initialState: arrayed ? configurationItems(out.initialState, out.stateIds) : out.initialState,
            finalState: arrayed ? configurationItems(out.finalState, out.stateIds) : out.finalState,
            visited: arrayed ? visitedItems(out.visited) : out.visited,
            neverEntered: arrayed ? stateItems(out.neverEntered, out.stateIds) : out.neverEntered,
            stateChanges: moves.length,
            stateChangesByEvent: movesByEvent,
            writes: writes.slice(0, MAX_WRITES_IN_DATA).map((w) => ({ atMs: toMs(w.tsNs), path: w.path, value: plain(w.value) })),
            writesTotal: writes.length,
            errors: errors.length,
            firstError: errors[0]?.message,
            unknownInputs: arrayed ? portNamesText(unknown, out.stateIds) : unknown,
            // Set aside, not replayed. The summary says so in words; this is the same fact as data,
            // so a next call can fix the connection rather than re-read the paragraph.
            skipped: arrayed
              ? skippedItems(out.skipped, out.stateIds)
              : out.skipped.map((s) => ({ name: s.name, path: s.path, reason: s.reason })),
            hung: out.hung,
            notes: out.notes,
          },
        };
      } catch (e) {
        // A dead worker, a frame that never came up: the replay did not happen, and the same call
        // may well work in a moment.
        return { ok: false, message: `The replay could not run: ${e instanceof Error ? e.message : String(e)}`, retryable: true };
      } finally {
        engine.dispose();
      }
    },
  };

  return registerClientTools(simulate);
}
